import { ChartOut, ChartStatus } from "../../api/client";
import { useJobStore } from "../../stores/jobStore";

// ── Pipeline stage helpers ─────────────────────────────────────────────────

const STAGES = ["Triage", "Detect", "OCR", "Extract", "Review"] as const;

function getStageIndex(chart: ChartOut): number {
  if (["COMPLETED", "APPROVED", "AUTO_APPROVED"].includes(chart.status)) return 5;
  if (chart.status === "NEEDS_REVIEW") return 4;
  if (chart.status === "FAILED") return -1;
  if (chart.status === "PROCESSING") {
    if (chart.zone_path) return 3;
    if (chart.triage_result) return 2;
    return 1;
  }
  return 0;
}

function PipelineSteps({ chart }: { chart: ChartOut }) {
  const stage = getStageIndex(chart);
  const failed = chart.status === "FAILED";

  return (
    <div className="flex items-center gap-0.5 w-full">
      {STAGES.map((label, i) => {
        const done    = stage > i && !failed;
        const current = stage === i + 1 && !failed && chart.status === "PROCESSING";
        const isHITL  = i === 4 && chart.status === "NEEDS_REVIEW";

        return (
          <div key={label} className="flex items-center flex-1 min-w-0">
            {i > 0 && (
              <div className={`h-px flex-1 transition-colors duration-500 ${done || isHITL ? "bg-blue-500" : "bg-rdp-border"}`} />
            )}
            <div
              title={label}
              className={[
                "w-2 h-2 rounded-full flex-shrink-0 transition-all duration-300",
                failed && i <= stage  ? "bg-red-500" :
                isHITL                ? "bg-amber-500 animate-pulse" :
                current               ? "bg-blue-400 animate-pulse scale-125" :
                done                  ? "bg-blue-500" :
                                        "bg-rdp-border",
              ].join(" ")}
            />
          </div>
        );
      })}
    </div>
  );
}

// ── Status badge config ────────────────────────────────────────────────────

const STATUS_BADGE: Record<ChartStatus, { bg: string; text: string; label: string }> = {
  PENDING:      { bg: "bg-slate-700/50",    text: "text-slate-400",   label: "Pending" },
  PROCESSING:   { bg: "bg-blue-500/15",     text: "text-blue-400",    label: "Processing" },
  AUTO_APPROVED:{ bg: "bg-emerald-500/15",  text: "text-emerald-400", label: "Auto ✓" },
  NEEDS_REVIEW: { bg: "bg-amber-500/15",    text: "text-amber-400",   label: "HITL" },
  APPROVED:     { bg: "bg-emerald-500/15",  text: "text-emerald-400", label: "Approved" },
  REJECTED:     { bg: "bg-red-500/15",      text: "text-red-400",     label: "Rejected" },
  COMPLETED:    { bg: "bg-emerald-500/20",  text: "text-emerald-300", label: "Done ✓" },
  FAILED:       { bg: "bg-red-500/15",      text: "text-red-400",     label: "Failed ✕" },
};

// ── Chart card ─────────────────────────────────────────────────────────────

function ChartCard({ chart, selected, onClick }: { chart: ChartOut; selected: boolean; onClick: () => void }) {
  const badge = STATUS_BADGE[chart.status] ?? STATUS_BADGE.PENDING;
  const conf  = chart.pipeline_confidence != null
    ? `${(chart.pipeline_confidence * 100).toFixed(0)}%`
    : null;

  const confColor = chart.pipeline_confidence == null ? "text-slate-600"
    : chart.pipeline_confidence >= 0.85 ? "text-emerald-400"
    : chart.pipeline_confidence >= 0.65 ? "text-amber-400"
    : "text-red-400";

  return (
    <button
      onClick={onClick}
      className={[
        "relative flex flex-col rounded-xl border overflow-hidden text-left",
        "transition-all duration-150 cursor-pointer group",
        "focus:outline-none focus:ring-1 focus:ring-blue-500",
        selected
          ? "border-blue-500 shadow-glow-blue"
          : chart.status === "NEEDS_REVIEW"
          ? "border-amber-500/50 hover:border-amber-400 hover:shadow-glow-amber"
          : "border-rdp-border hover:border-blue-500/40 hover:shadow-glow-blue",
      ].join(" ")}
    >
      {/* Thumbnail */}
      <div className="relative bg-rdp-border/50 w-full aspect-[4/3] flex items-center justify-center overflow-hidden">
        {chart.thumbnail_s3_key ? (
          <img
            src={`/media/jobs/${chart.job_id}/charts/${chart.id}/thumbnail`}
            alt={`Page ${chart.page_num + 1} chart ${chart.chart_idx + 1}`}
            className="object-contain w-full h-full opacity-90 group-hover:opacity-100 transition-opacity"
            loading="lazy"
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-rdp-border">
            <span className="text-2xl">📈</span>
            {chart.status === "PROCESSING" && (
              <div className="w-4 h-4 border-2 border-rdp-border border-t-blue-500 rounded-full animate-spin" />
            )}
          </div>
        )}

        {/* HITL badge */}
        {chart.status === "NEEDS_REVIEW" && (
          <span className="absolute top-1.5 right-1.5 bg-amber-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">
            HITL
          </span>
        )}
      </div>

      {/* Info */}
      <div className="p-2 bg-rdp-card space-y-2">
        <div className="flex items-center justify-between gap-1">
          <span className="text-xs text-slate-300 font-medium">
            P{chart.page_num + 1}·C{chart.chart_idx + 1}
          </span>
          {conf && (
            <span className={`text-[10px] font-mono font-semibold ${confColor}`}>{conf}</span>
          )}
        </div>

        {/* Category + Session badges */}
        {(chart.chart_category || chart.session_id) && (
          <div className="flex items-center gap-1 flex-wrap">
            {chart.chart_category && chart.chart_category !== "unknown" && (
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                chart.chart_category === "landing"
                  ? "bg-emerald-500/20 text-emerald-400"
                  : "bg-sky-500/20 text-sky-400"
              }`}>
                {chart.chart_category.toUpperCase()}
              </span>
            )}
            {chart.session_id && (
              <span className="text-[9px] font-mono text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded">
                §{chart.session_id}
              </span>
            )}
          </div>
        )}

        <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>
          {badge.label}
        </span>

        {/* Pipeline steps */}
        <PipelineSteps chart={chart} />

        {chart.zone_path && (
          <p className="text-[9px] text-slate-600 font-mono truncate">{chart.zone_path}</p>
        )}
      </div>
    </button>
  );
}

// ── Gallery ────────────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<string, { label: string; color: string; border: string }> = {
  landing: { label: "Landing",  color: "text-emerald-400", border: "border-emerald-500/30" },
  takeoff: { label: "Takeoff",  color: "text-sky-400",     border: "border-sky-500/30"     },
  unknown: { label: "Unknown",  color: "text-slate-400",   border: "border-slate-500/30"   },
};

function ChartGrid({ charts }: { charts: ChartOut[] }) {
  const { selectedChart, selectChart } = useJobStore();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 p-4 animate-slide-up">
      {charts.map((chart) => (
        <ChartCard
          key={chart.id}
          chart={chart}
          selected={selectedChart?.id === chart.id}
          onClick={() => selectChart(chart)}
        />
      ))}
    </div>
  );
}

export function ChartGallery({ charts }: { charts: ChartOut[] }) {
  // Hide pages that were skipped as non-chart content
  const visible = charts.filter(
    (c) => !(c.status === "FAILED" && (c.gate_failure as Record<string, unknown>)?.reason === "not_chart")
  );

  if (!charts.length) {
    return (
      <div className="flex flex-col items-center justify-center h-32 gap-2 text-slate-600">
        <span className="text-2xl">🔍</span>
        <span className="text-xs">No charts extracted yet</span>
      </div>
    );
  }

  if (!visible.length) {
    return (
      <div className="flex flex-col items-center justify-center h-32 gap-2 text-slate-600">
        <span className="text-2xl">📄</span>
        <span className="text-xs">No charts found in this PDF</span>
      </div>
    );
  }

  // If intake metadata present → grouped view
  const hasIntake = visible.some((c) => c.chart_category && c.chart_category !== "unknown");
  if (hasIntake) {
    // Group: category → session → charts
    const categoryOrder = ["landing", "takeoff", "unknown"];
    const byCategory = new Map<string, Map<string, ChartOut[]>>();
    for (const chart of visible) {
      const cat = chart.chart_category ?? "unknown";
      const sess = chart.session_id ?? "—";
      if (!byCategory.has(cat)) byCategory.set(cat, new Map());
      const bySess = byCategory.get(cat)!;
      if (!bySess.has(sess)) bySess.set(sess, []);
      bySess.get(sess)!.push(chart);
    }

    return (
      <div className="flex flex-col pb-4">
        {categoryOrder.filter((cat) => byCategory.has(cat)).map((cat) => {
          const cfg = CATEGORY_CONFIG[cat] ?? CATEGORY_CONFIG.unknown;
          const sessions = byCategory.get(cat)!;
          const total = [...sessions.values()].reduce((s, a) => s + a.length, 0);

          return (
            <div key={cat} className="mt-4">
              {/* Category header */}
              <div className={`flex items-center gap-2 mx-4 mb-2 pb-1.5 border-b ${cfg.border}`}>
                <span className={`text-xs font-bold uppercase tracking-widest ${cfg.color}`}>
                  {cfg.label}
                </span>
                <span className="text-[10px] text-slate-600">{total} chart{total !== 1 ? "s" : ""}</span>
              </div>

              {/* Sessions within this category */}
              {[...sessions.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([sess, sessCharts]) => (
                <div key={sess} className="mb-3">
                  {sess !== "—" && (
                    <p className="text-[10px] font-mono text-slate-500 px-4 pb-1">
                      Section {sess} · {sessCharts.length} chart{sessCharts.length !== 1 ? "s" : ""}
                    </p>
                  )}
                  <ChartGrid charts={sessCharts} />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  // Flat grid (no intake metadata)
  return (
    <div className="flex flex-col gap-1">
      <ChartGrid charts={visible} />
    </div>
  );
}
